import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, defer, EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { catchError, expand, finalize, map, switchMap, takeLast, takeWhile, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { NicaToastService } from './nica-toast.service';
import { AzioniFirDigitaliKey } from '../views/gestione-formulari/interfaces';
import { Utenteservice } from './utente.service';
import { custom } from 'devextreme/ui/dialog';

export type DataFirma = {
  certificato: string
  token: string
  firma: string
  identificativo_utente?: string
  tipo_firma?: 'Produttore' | 'Trasportatore' | 'ProduttoreTrasportatore' |
  'Accettazione' | 'Annotazione' | 'Annullamento' | 'TrasbordoTotale' | 'TrasbordoParziale' |
  'SostaTecnica' | 'DestinatarioSuccessivo' | 'AccettazioneSuccessiva'
}

export type GeneraHashResult = {
  esito: {
    hash_algorithm: string
    digest_to_sign: string
    token: string
    versione: string
  }
  tipo: string
  transazione_id: string
  validazione: Array<any>
  errore: boolean
  tempo_elaborazione: string
}
export type AutorizzaCredenzialiResp = {
  sad: string
  expires_in: number
  handle: string
  errore: string
}

export interface DocumentTask {
  numeroFormulario: string;
  idSoggetto: number;
  endpoint: string;
  processId: AzioniFirDigitaliKey | 'InvioFirDigitale' | 'EliminaAllegato'
  title: string,
  credentials_id?: any,
  status?: 'idle' | 'queued' | 'running' | 'pending' | 'completed' | 'error'
  getTransaction_id?: () => Promise<{ transazione_id: string }>
}

export interface DocumentState {
  status: 'idle' | 'queued' | 'running' | 'pending' | 'completed' | 'error';
  lastResult: any[];
  lastError: any[];
  listTask: DocumentTask[],
  lastTask?: string
}

export interface TransactionStateMap {
  [numeroFormulario: string]: DocumentState;
}
export interface CredenzialiInfo {
  credentials_id: string
  description: string
  signature_qualifier: string
  key: {
    status: string
    algo: Array<string>
    len: number
    curve: string
  }
  cert: {
    status: string
    certificates: Array<string>
    issuer_dn: string
    serial_number: string
    subject_dn: string
    valid_from: string
    valid_to: string
  }
  auth: {
    mode: string
    expression: string
    objects: Array<{
      type: string
      id: string
      format: string
      generator: string
      label: string
      description: string
    }>
  }
  scal: string
  multisign: number
  lang: string
}

export interface GetCredenzialiInfo {
  idSoggetto: number,
  credentials_id: string,
}

export interface IAuthorizeCredentialsRequest {
  credentials_id: string,
  num_signatures: number,
  hashes: any[],
  hash_algo: '2.16.840.1.101.3.4.2.1',
  description: string,
  auth_data: { id: 'mobile' }[]
}
export type ISignHashRequest = {
  credentials_id: string
  sad: string
  hashes: Array<string>
  hash_algo: '2.16.840.1.101.3.4.2.1',
  sign_algo: "1.2.840.10045.4.3.2"
}


@Injectable({
  providedIn: 'root'
})
export class FirDigitaliTransactionService {
  private readonly pollIntervalMs = 10000;
  private readonly httpClientNoInterceptor: HttpClient;
  private readonly header = (token: string, content = 'application/json') => {
    return new HttpHeaders().set("Authorization", 'bearer ' + token)
      .set('Content-Type', `${content}`);
  }
  public messaggioFirma;

  /** Coda globale delle azioni in attesa */
  private readonly globalQueueSubject: BehaviorSubject<DocumentTask[]> = new BehaviorSubject<DocumentTask[]>([]);
  readonly globalQueue$ = this.globalQueueSubject.asObservable();
  /** Coda logica per documento */
  private readonly documentQueues = new Map<string, DocumentTask[]>();

  /** Task attualmente in esecuzione */
  private readonly currentTaskSubject: BehaviorSubject<DocumentTask | null> = new BehaviorSubject<DocumentTask | null>(null);
  readonly currentTask$ = this.currentTaskSubject.asObservable()
  /** Se un processo con transaction è in atto */
  private readonly isProcessingSubject = new BehaviorSubject<boolean>(false);
  readonly isProcessing$ = this.isProcessingSubject.asObservable()
  /** Stato globale delle azioni eseguite che hanno ricevuto un esito  */
  private readonly stateSubject = new BehaviorSubject<TransactionStateMap>({});
  readonly state$ = this.stateSubject.asObservable();

  private readonly lastFirmaSubject: BehaviorSubject<DocumentTask & { data: { [key: string]: any }, status: DocumentState['status'] } | null> = new BehaviorSubject<DocumentTask & { data: { [key: string]: any }, status: DocumentState['status'] } | null>(null);
  readonly lastFirma$ = this.lastFirmaSubject.asObservable()

  constructor(private readonly httpClient: HttpClient, private readonly NicaToastService: NicaToastService, private httpClientBack: HttpBackend, private readonly Utenteservice: Utenteservice) {
    this.httpClientNoInterceptor = new HttpClient(httpClientBack)
  }

  async enqueueAction({ endpoint, idSoggetto, numeroFormulario, getTransaction_id, title, processId, credentials_id }: DocumentTask): Promise<void> {
    const listTask = this.documentQueues.get(numeroFormulario) || [];
    const taskState = this.getFirState(numeroFormulario);

    const isRunning =
      this.currentTaskSubject.value &&
      this.currentTaskSubject.value.numeroFormulario === numeroFormulario &&
      this.currentTaskSubject.value.endpoint === endpoint;

    const isQueued = listTask.some((t) => t.endpoint === endpoint);

    if (isRunning || isQueued) {
      return;
    }

    const newTask: DocumentTask = { endpoint, idSoggetto, numeroFormulario, getTransaction_id, title, processId, credentials_id };

    if (this.currentTaskSubject.getValue() && this.currentTaskSubject.getValue().processId === 'Annulla') {
      this.NicaToastService.addToast({ message: `${newTask.title} - Elaborazione bloccata per annullamento in corso`, type: 'warning' })
      return
    }
    if (this.currentTaskSubject.getValue() && this.currentTaskSubject.getValue().processId === 'RollbackFirma') {
      this.NicaToastService.addToast({ message: `${newTask.title} - Elaborazione bloccata per rollback firma in corso`, type: 'warning' })
      return
    }
    if (this.currentTaskSubject.getValue() && this.currentTaskSubject.getValue().processId.includes('AcquisizioneFirma')) {
      this.NicaToastService.addToast({ message: `${newTask.title} - Elaborazione bloccata per firma in corso`, type: 'warning' })
      return
    }

    const listTaskQueue = [...listTask, newTask];
    this.documentQueues.set(numeroFormulario, listTaskQueue);

    const queueStory = this.globalQueueSubject.getValue()
    this.globalQueueSubject.next([...queueStory, newTask])

    if (!taskState || taskState.status === 'idle' || taskState.status === 'completed' || taskState.status === 'error') {
      this.updateState(newTask, { status: 'queued', lastError: [], lastResult: [] });
    }
    this.NicaToastService.addToast({ message: `${newTask.title} - Elaborazione in corso`, type: 'info' })

    this.scheduleNext();
  }

  getFirState(numeroFormulario: string): DocumentState | undefined {
    return this.stateSubject.value[numeroFormulario];
  }

  isFirInFirma() {
    return this.currentTaskSubject.getValue().processId.includes('AcquisizioneFirma')
  }

  //!---------------------------------------------------//!---------------------------------------------------//!-----------------------------
  private async scheduleNext(): Promise<void> {
    if (this.isProcessingSubject.value) return;
    if (this.currentTaskSubject.value) return;

    const queue = this.globalQueueSubject.getValue();

    const next = queue.length > 0 ? queue[0] : null;
    if (!next) return;

    if (next.processId === 'Annulla') {
      this.globalQueueSubject.next(queue.slice(1).map((task) => {
        if (task.numeroFormulario === next.numeroFormulario) {
          if (task.processId !== next.processId) this.NicaToastService.addToast({
            message: `${task.title} - Elaborazione bloccata per annullamento in corso`, type: 'warning'
          });
        }
        else return task
      }).filter(task => task !== null && task !== undefined));
    } else if (next.processId === 'RollbackFirma') {
      this.globalQueueSubject.next(queue.slice(1).map((task) => {
        if (task.numeroFormulario === next.numeroFormulario) {
          if (task.processId !== next.processId) this.NicaToastService.addToast({
            message: `${task.title} - Elaborazione bloccata per rollback firma in corso`, type: 'warning'
          });
        }
        else return task
      }).filter(task => task !== null && task !== undefined));
    } else if (next.processId.includes('AcquisizioneFirma')) {
      this.globalQueueSubject.next(queue.slice(1).map((task) => {
        if (task.numeroFormulario === next.numeroFormulario) {
          if (task.processId !== next.processId) this.NicaToastService.addToast({
            message: `${task.title} - Elaborazione bloccata per firma in corso`, type: 'warning'
          });
        }
        else return task
      }).filter(task => task !== null && task !== undefined));
    } else {
      this.globalQueueSubject.next(queue.slice(1));
    }

    this.currentTaskSubject.next(next);
    this.isProcessingSubject.next(true);

    this.updateState(next, { status: 'running', lastError: [], lastResult: [] });

    const process = next.processId.includes('AcquisizioneFirma') ? await this.processFirma(next) : await this.processTask(next)
    process.pipe(
      tap((result) => {
        if (result instanceof Error) return throwError(() => new Error('PT-3'));
        if (result !== null && result !== undefined) {
          this.updateState(next, {
            status: 'completed',
            lastResult: result,
            lastError: []
          });
        }
      }),
      catchError((err) => {
        this.updateState(next, {
          status: 'error',
          lastError: err,
          lastResult: []
        });
        return of(null);
      }),
      finalize(() => {
        const docQueue = this.documentQueues.get(next.numeroFormulario) || [];
        const filteredQueue = docQueue.filter((t) => t !== next);
        if (filteredQueue.length === 0) {
          this.documentQueues.delete(next.numeroFormulario);
        } else {
          this.documentQueues.set(next.numeroFormulario, filteredQueue);
        }

        this.currentTaskSubject.next(null);
        this.isProcessingSubject.next(false);

        const docState = this.getFirState(next.numeroFormulario);
        if (docState && (docState.status === 'completed' || docState.status === 'error')) {
          if (docState.status === 'completed') {
            this.NicaToastService.addToast({ message: `${next.title} - Completato con successo`, type: 'success' })
          }
          if (docState.status === 'error') {
            this.NicaToastService.addToast({ message: `${next.title} - Errore nell'elaborazione`, type: 'error' })
          }
        } else if (docState && docState.status !== 'queued') {
          this.updateState(next, { status: 'idle', lastError: [], lastResult: [] });
        }

        this.scheduleNext();
      })
    ).subscribe();
  }

  private async processTask(task: DocumentTask): Promise<Observable<any[] | Error>> {
    try {
      const { idSoggetto, numeroFormulario, getTransaction_id } = task;
      const { transazione_id } = await getTransaction_id()
      if (!transazione_id || !idSoggetto) return throwError(() => new Error('PT-1'));

      this.updateState(task, { status: 'pending', lastError: [], lastResult: [] });

      return this.pollStatus({ idSoggetto, transazione_id }).pipe(
        switchMap(res => {
          if (res.status !== 'completed') {
            return throwError(() => new Error('PT-2'));
          }
          return this.GetResultTransazione({ idSoggetto, transazione_id });
        }),
        switchMap(res => {
          if (res.status !== 'completed') {
            return throwError(() => new Error('PT-3'));
          }
          return of(res.lastResult);
        })
      );
    } catch (errore) {
      return throwError(() => new Error(errore));
    }
  }
  private async processFirma(task: DocumentTask): Promise<Observable<any[] | Error>> {
    try {
      const { idSoggetto, processId, credentials_id, numeroFormulario, endpoint, title } = task;
      if (!processId || !idSoggetto || !credentials_id) return this.failFirma('PF-1');

      this.updateState(task, { status: 'pending', lastError: [], lastResult: [] });

      this.lastFirmaSubject.next({ idSoggetto, processId, credentials_id, numeroFormulario, endpoint, title, data: {}, status: 'pending' })
      this.messaggioFirma = custom({
        messageHtml: `
    <div style="margin-top: 27px;" class="px-3">
      <p>
        Il processo di firma digitale è in corso.<br/>
        Riceverai una notifica sul dispositivo accreditato per autorizzare l’operazione.<br/>
        <b>La conferma deve essere effettuata entro 1 minuto.</b> In caso contrario, sarà necessario ripetere l’intero processo di firma.
      </p>
        <p class="m-0">
          <b>
            Tempo residuo: <span id="firma-timer">60</span> secondi
          </b>
        </p>
      <div class="progress mt-2" role="progressbar"
        aria-valuemin="0" aria-valuemax="60">
        <div
          id="firma-progress"
          class="progress-bar progress-bar-striped progress-bar-animated"
          style="width: 100%; background-color: #0167B0 !important">
        </div>
      </div>
    </div>
  `,
        showTitle: true,
        title: 'Acquisizione Firma',
        dragEnabled: false,
        buttons: [{ visible: false }]
      })
      this.messaggioFirma.show()
      return this.GetCredenzialiInfo({ credentials_id, idSoggetto }).pipe(
        catchError((err) => this.failFirma(err)),
        switchMap(res => {
          if (!res) return this.failFirma('PF-2');
          const firma = this.lastFirmaSubject.getValue();
          const formData: FormData = new FormData()
          formData.append('certificatoFirmaRemotaBase64', firma.data.GetCredenzialiInfo.cert.certificates[0])
          switch (firma.processId) {
            case 'AcquisizioneFirmaProduttoreTrasportatore':
              formData.append('tipo_firma', 'Produttore');
              break;
            default:
              break;
          }
          return this.GeneraHash({ idSoggetto, numero_fir: numeroFormulario, formData }).pipe(
            catchError((err) => this.failFirma(err)))
        }),
        switchMap(res => {
          if (res.status !== 'completed') return this.failFirma('PF-3');
          const firma = this.lastFirmaSubject.getValue();
          const autorizzazione: IAuthorizeCredentialsRequest = {
            credentials_id: firma.credentials_id,
            num_signatures: 1,
            hashes: [firma.data.GeneraHash.esito.digest_to_sign],
            hash_algo: '2.16.840.1.101.3.4.2.1',
            auth_data: [{ id: 'mobile' }],
            description: "X-FIR : " + firma.numeroFormulario,
          }
          return this.AutorizzaCredenziali({ idSoggetto: firma.idSoggetto, autorizza: autorizzazione }).pipe(
            catchError((err) => this.failFirma(err)))
        }),
        switchMap((res) => {
          if (!res) return this.failFirma('PF-4');
          return this.VerificaAutorizzazioneCredenziali({ idSoggetto, handle: res.handle }).pipe(
            catchError((err) => this.failFirma(err)))
        }),
        switchMap((res) => {
          if (!res) return this.failFirma('PF-5');
          const firma = this.lastFirmaSubject.getValue();
          const SignHash: ISignHashRequest = {
            credentials_id: firma.credentials_id,
            sad: firma.data.VerificaAutorizzazioneCredenziali.sad,
            hashes: [firma.data.GeneraHash.esito.digest_to_sign],
            hash_algo: '2.16.840.1.101.3.4.2.1',
            sign_algo: "1.2.840.10045.4.3.2"
          }
          return this.SignHash({ idSoggetto, SignHash }).pipe(
            catchError((err) => this.failFirma(err)))
        }),
        switchMap((res) => {
          if (!res) return this.failFirma('PF-6');
          const firma = this.lastFirmaSubject.getValue();
          let tipo_firma: DataFirma['tipo_firma'] = undefined
          switch (firma.processId) {
            case 'AcquisizioneFirmaAccettazioneSuccessiva':
              tipo_firma = 'AccettazioneSuccessiva'
              break;
            case 'AcquisizioneFirmaAnnotazione':
              tipo_firma = 'Annotazione';
              break;
            case 'AcquisizioneFirmaAnnullamento':
              tipo_firma = 'Annullamento';
              break;
            case 'AcquisizioneFirmaDestinatario':
              tipo_firma = 'Accettazione';
              break;
            case 'AcquisizioneFirmaDestinatarioSuccessivo':
              tipo_firma = 'DestinatarioSuccessivo';
              break;
            case 'AcquisizioneFirmaProduttore':
            case 'AcquisizioneFirmaProduttoreTrasportatore':
              tipo_firma = 'Produttore';
              break;
            case 'AcquisizioneFirmaTrasportatore':
              tipo_firma = 'Trasportatore';
              break
            default:
              break;
          }
          const dataToSend: DataFirma = {
            certificato: firma.data.GetCredenzialiInfo.cert.certificates[0],
            firma: firma.data.SignHash.signatures[0],
            tipo_firma,
            token: firma.data.GeneraHash.esito.token
          }
          return this.Firma({ idSoggetto: firma.idSoggetto, numero_fir: firma.numeroFormulario, dataToSend }).pipe(
            catchError((err) => this.failFirma(err)))
        }),
        switchMap(res => {
          if (res.status !== 'completed') return this.failFirma('PF-7');
          const lastFirma = this.lastFirmaSubject.getValue();
          this.lastFirmaSubject.next({
            ...lastFirma,
            status: 'completed'
          });
          return of([lastFirma.data]);
        })
      )
    } catch {
      return this.failFirma('PT-0');
    }
  }

  private pollStatus({ idSoggetto, transazione_id }: Pick<DocumentTask, 'idSoggetto'> & { transazione_id: string }): Observable<Partial<DocumentState>> {
    return this.GetStatusTransazione({ idSoggetto, transazione_id }).pipe(
      expand(res => {
        if (res.status === 'pending') {
          return timer(this.pollIntervalMs).pipe(switchMap(() => {
            if (res.status === 'pending') return this.GetStatusTransazione({ idSoggetto, transazione_id })
          }))
        } else {
          return EMPTY
        }
      }),
      takeLast(1)
    );
  }

  private failFirma(code: string): Observable<never> {
    const firma = this.lastFirmaSubject.getValue();
    this.lastFirmaSubject.next({ ...firma, status: 'error' });
    if (this.messaggioFirma) this.messaggioFirma.hide();
    this.messaggioFirma = undefined
    return throwError(() => new Error(code));
  }

  private updateState(next: DocumentTask, patch: Pick<DocumentState, 'status' | 'lastError' | 'lastResult'>): void {
    const current = this.stateSubject.value;

    const prevState: DocumentState = current[next.numeroFormulario] || {
      status: 'idle',
      listTask: [],
      lastError: [], lastResult: []
    };
    next.status = patch.status
    const exists = prevState.listTask.some(t => t.processId === next.processId);

    const listTask = exists
      ? prevState.listTask.map(t => t.processId === next.processId ? next : t)
      : [...prevState.listTask, next];
    const lastError = prevState.lastError
      ? [...prevState.lastError, ...patch.lastError] : patch.lastError ? [...patch.lastError] : [];
    const lastResult = prevState.lastResult
      ? [...prevState.lastResult, ...patch.lastResult] : patch.lastResult ? [...patch.lastResult] : [];

    const updated: DocumentState = {
      ...prevState,
      ...patch,     // patch dello stato (override)
      listTask,
      lastTask: next.processId,
      lastError,
      lastResult
    };

    this.stateSubject.next({
      ...current,
      [next.numeroFormulario]: updated,
    });
  }


  private GetStatusTransazione({ idSoggetto, transazione_id }: Pick<DocumentTask, 'idSoggetto'> & { transazione_id: string }): Observable<Partial<DocumentState>> {
    if (!transazione_id || !idSoggetto) return of({ status: 'error', lastError: ['GST-1'] });
    return this.httpClientNoInterceptor.get<any>(`${environment.BASE_API_URL}rentri/fir-digitali/GetStatusTransazione/${transazione_id}?idSoggetto=${idSoggetto}`,
      { observe: 'response', responseType: 'json', headers: this.header(this.Utenteservice.DammiToken()) }
    ).pipe(
      map(response => {
        if (response.status === 202) return { status: 'pending' } as DocumentState;
        if (response.status === 200) return { status: 'completed', lastResult: [response.body] } as DocumentState
        else return { status: 'error', lastError: ['GST-2'] } as DocumentState;
      }),
      catchError(err => {
        if (err.status === 202) return of({ status: 'pending' } as DocumentState);
        if (err.status === 200) return of({ status: 'completed', lastResult: err.body } as DocumentState);

        return of({ status: 'error', lastError: ['GST-3'] } as DocumentState);
      })
    );
  }


  private GetResultTransazione({ idSoggetto, transazione_id }: Pick<DocumentTask, 'idSoggetto'> & { transazione_id: string }): Observable<Partial<DocumentState>> {
    if (!transazione_id || !idSoggetto) return of({ status: 'error', lastError: ['GRT-1'] });
    return this.httpClientNoInterceptor.get<any>(`${environment.BASE_API_URL}rentri/fir-digitali/GetResultTransazione/${transazione_id}?idSoggetto=${idSoggetto}`,
      { observe: 'response', headers: this.header(this.Utenteservice.DammiToken()) }
    ).pipe(
      map(response => {
        if (response.status === 200) return { status: 'completed', lastResult: [response.body] } as DocumentState;
        return { status: 'error', lastError: [response.body] } as DocumentState;
      }),
      catchError(() => of({ status: 'error', lastError: ['GRT-3'] } as DocumentState))
    );
  }
  //! Firma 
  private GetCredenzialiInfo({ credentials_id, idSoggetto }: GetCredenzialiInfo): Observable<CredenzialiInfo> {
    if (!credentials_id || !idSoggetto) return throwError(() => new Error('GCO-1'));
    const query = new URLSearchParams({ credentials_id, idSoggetto: idSoggetto.toString() })
    return this.httpClientNoInterceptor.get<CredenzialiInfo>(`${environment.BASE_API_URL}ca-rentri/GetCredenzialiInfo?${query}`, { headers: this.header(this.Utenteservice.DammiToken()) }).pipe(
      tap((res) => {
        const firma = this.lastFirmaSubject.getValue();
        this.lastFirmaSubject.next({
          ...firma,
          data: { ...firma.data, GetCredenzialiInfo: res }
        });

      }),
      catchError(() => throwError(() => new Error('GCO-2')))
    )
  }
  private AutorizzaCredenziali({ autorizza, idSoggetto }: { autorizza: IAuthorizeCredentialsRequest, idSoggetto: number }): Observable<AutorizzaCredenzialiResp> {
    if (!autorizza || !idSoggetto) return throwError(() => new Error('AC-1'));
    const query = new URLSearchParams({ idSoggetto: idSoggetto.toString() })
    return this.httpClientNoInterceptor.post<AutorizzaCredenzialiResp>(`${environment.BASE_API_URL}ca-rentri/AutorizzaCredenziali?${query}`, autorizza, { headers: this.header(this.Utenteservice.DammiToken()) }).pipe(
      tap((res) => {
        const firma = this.lastFirmaSubject.getValue();
        this.lastFirmaSubject.next({
          ...firma,
          data: { ...firma.data, AutorizzaCredenziali: res }
        });
      }),
      catchError(() => throwError(() => new Error('AC-2')))
    )
  }
  private VerificaAutorizzazioneCredenziali({ handle, idSoggetto }: { handle: string, idSoggetto: number }): Observable<AutorizzaCredenzialiResp> {
    if (!handle || !idSoggetto) return throwError(() => new Error('VAC-1'));
    const query = new URLSearchParams({ idSoggetto: idSoggetto.toString(), handle, loop: 'true' })
    setTimeout(() => {
      let remaining = 60;
      const timerEl = document.getElementById('firma-timer');
      const progressEl = document.getElementById('firma-progress');

      const interval = setInterval(() => {
        remaining--;
        if (timerEl) timerEl.textContent = remaining.toString();
        if (progressEl) progressEl.style.width = `${(remaining / 60) * 100}%`;

        if (remaining <= 0) {
          clearInterval(interval);
          this.messaggioFirma.hide();
          this.messaggioFirma = undefined
        }
      }, 1000);

      this.messaggioFirma.onHidden = () => clearInterval(interval);
    });
    return this.httpClientNoInterceptor.post<AutorizzaCredenzialiResp>(`${environment.BASE_API_URL}ca-rentri/VerificaAutorizzazioneCredenziali?${query}`, null, { headers: this.header(this.Utenteservice.DammiToken()) }).pipe(
      tap((res) => {
        const firma = this.lastFirmaSubject.getValue();
        this.lastFirmaSubject.next({
          ...firma,
          data: { ...firma.data, VerificaAutorizzazioneCredenziali: res }
        });
      }),
      catchError(() => throwError(() => new Error('VAC-2')))
    )
  }
  private SignHash({ SignHash, idSoggetto }: { SignHash: ISignHashRequest, idSoggetto: number }): Observable<{ signatures: string[] }> {
    if (!SignHash || !idSoggetto) return throwError(() => new Error('SH-1'));
    const query = new URLSearchParams({ idSoggetto: idSoggetto.toString() })
    return this.httpClientNoInterceptor.post<{ signatures: string[] }>(`${environment.BASE_API_URL}ca-rentri/SignHash?${query}`, SignHash, { headers: this.header(this.Utenteservice.DammiToken()) }).pipe(
      tap((res) => {
        const firma = this.lastFirmaSubject.getValue();
        this.lastFirmaSubject.next({
          ...firma,
          data: { ...firma.data, SignHash: res }
        });
      }),
      catchError(() => throwError(() => new Error('SH-2')))
    )
  }

  private GeneraHash({ idSoggetto, numero_fir, formData }: { idSoggetto: number, numero_fir: string, formData: FormData }): Observable<Partial<DocumentState>> {
    if (!numero_fir || !idSoggetto) return of({ status: 'error', lastError: ['GH-1'] } as DocumentState);
    return this.httpClient.post<{ transazione_id: string }>(`${environment.BASE_API_URL}rentri/fir-digitali/${numero_fir}/GeneraHash?idSoggetto=${idSoggetto}`, formData).pipe(
      switchMap(({ transazione_id }) => {
        return this.pollStatus({ idSoggetto, transazione_id }).pipe(map(state => ({ state, transazione_id })))
      }),
      switchMap(({ state, transazione_id }) => {
        if (state.status !== 'completed') {
          return of({ status: 'error', lastError: ['GH-2'] });
        }
        return this.GetResultTransazione({ idSoggetto, transazione_id });
      }),
      switchMap(res => {
        if (res.status !== 'completed') {
          return of({ status: 'error', lastError: ['GH-3'] } as DocumentState);
        }
        const firma = this.lastFirmaSubject.getValue();
        this.lastFirmaSubject.next({
          ...firma,
          data: { ...firma.data, GeneraHash: res.lastResult[0] as GeneraHashResult }
        });
        return of(res);
      }),
      catchError(() => of({ status: 'error', lastError: ['GH-4'] } as DocumentState))
    )
  }
  private Firma({ idSoggetto, numero_fir, dataToSend }: { idSoggetto: number, numero_fir: string, dataToSend: DataFirma }): Observable<Partial<DocumentState>> {
    if (!numero_fir || !idSoggetto || !dataToSend) return of({ status: 'error', lastError: ['FF-1'] } as DocumentState);
    return this.httpClientNoInterceptor.post<{ transazione_id: string }>(`${environment.BASE_API_URL}rentri/fir-digitali/${numero_fir}/Firma?idSoggetto=${idSoggetto}`, dataToSend, { headers: this.header(this.Utenteservice.DammiToken()) }).pipe(
      switchMap(({ transazione_id }) => {
        return this.pollStatus({ idSoggetto, transazione_id }).pipe(map(state => ({ state, transazione_id })))
      }),
      switchMap(({ state, transazione_id }) => {
        if (state.status !== 'completed') {
          return of({ status: 'error', lastError: ['FF-2'] });
        }
        return this.GetResultTransazione({ idSoggetto, transazione_id });
      }),
      switchMap(res => {
        if (res.status !== 'completed') {
          return of({ status: 'error', lastError: ['FF-3'] } as DocumentState);
        }
        const firma = this.lastFirmaSubject.getValue();
        this.lastFirmaSubject.next({
          ...firma,
          data: { ...firma.data, Firma: res.lastResult[0] }
        });
        return of(res);
      }),
      catchError(() => of({ status: 'error', lastError: ['FF-4'] } as DocumentState))
    )
  }

}
